import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ConnectionService from '../../services/ConnectionService';

function Login() {
  const { login, loginWithPostData, error, loading: authLoading, isAuthenticated } = useAuth();
  const [token, setToken] = useState('');
  const [userData, setUserData] = useState('');
  const [serverData, setServerData] = useState('');
  const [wsConnection, setWsConnection] = useState('');
  const [useAdvancedMode, setUseAdvancedMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [postAuthAttempted, setPostAuthAttempted] = useState(false);
  const [localError, setError] = useState(null); // Local error state for URL parameter errors
  const navigate = useNavigate();

  // Handle form-based authentication
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    let success = false;
    
    // Normal authentication flow
    if (useAdvancedMode) {
        // Advanced mode - use POST request format with user and server data
        try {
          // Parse user data JSON if provided
          const parsedUserData = userData ? JSON.parse(userData) : {};
          
          // Parse server data JSON if provided
          let parsedServerData = [];
          if (serverData) {
            parsedServerData = JSON.parse(serverData);
            // Ensure it's an array
            if (!Array.isArray(parsedServerData)) {
              parsedServerData = [parsedServerData];
            }
          }
          
          // Parse WS connection JSON if provided
          let parsedWsConnection = {};
          if (wsConnection) {
            parsedWsConnection = JSON.parse(wsConnection);
          }
          
          // Create the full POST request data using ConnectionService to ensure proper format
          const postData = ConnectionService.formatPostData({
            token: token,
            userData: parsedUserData,
            serverData: parsedServerData,
            wsConnection: parsedWsConnection
          });
          
          // Log the WebSocket connection info to debug
          console.log('%c Login: WebSocket connection info being sent:', 'background: #4CAF50; color: white; padding: 2px 5px; border-radius: 3px;');
          console.log('Login: wsConnection:', JSON.stringify(parsedWsConnection, null, 2));
          
          // Log the formatted data
          console.log('%c Login: POST data formatted according to README', 'background: #4CAF50; color: white; padding: 2px 5px; border-radius: 3px;');
          console.log('Login: Formatted POST data:', JSON.stringify(postData, null, 2));
          
          console.log('%c Login: Prepared POST data for authentication', 'background: #4CAF50; color: white; padding: 2px 5px; border-radius: 3px;');
          console.log('Login: POST data:', JSON.stringify(postData, null, 2));
          
          // Ensure the WebSocket connection information is included in the POST data
          if (!postData.ws_connection && parsedWsConnection) {
            console.log('%c Login: Adding WebSocket connection info to POST data', 'background: #FF9800; color: white; padding: 2px 5px; border-radius: 3px;');
            postData.ws_connection = parsedWsConnection;
          } else {
            console.log('%c Login: WebSocket connection info not present in POST data', 'background:rgb(255, 0, 0); color: white; padding: 2px 5px; border-radius: 3px;');
          }
          
          success = await loginWithPostData(postData);
        } catch (err) {
          console.error('Error parsing JSON data:', err);
          alert('Invalid JSON format. Please check your user and server data.');
          setLoading(false);
          return;
        }
      } else {
        // Simple mode - just use token
        success = await login(token);
      }
    
    setLoading(false);
    
    if (success) {
      // Redirect to dashboard on successful login
      navigate('/');
    }
  };

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Handle POST request authentication
  useEffect(() => {
    const handlePostAuth = async () => {
      // Only attempt POST auth once
      if (postAuthAttempted) return;
      setPostAuthAttempted(true);

      // Check if we have POST data in the URL
      const params = new URLSearchParams(window.location.search);
      const authData = params.get('authData');
      
      if (authData) {
        console.log('Found authentication data in URL parameter');
        try {
          // Parse the auth data
          const parsedData = JSON.parse(decodeURIComponent(authData));
          console.log('Successfully parsed authentication data from URL parameter:', parsedData);
          
          setLoading(true);
          const success = await loginWithPostData(parsedData);
          setLoading(false);
          
          if (success) {
            console.log('Authentication successful, redirecting to dashboard');
            navigate('/');
          } else {
            console.error('Authentication failed, showing error');
            // Error handling is done by the login context already
            // The error will be displayed in the login form
          }
        } catch (err) {
          console.error('Error processing URL parameter authentication data:', err);
          setError('Invalid JSON data format in URL parameter');
        }
      }
    };

    // Check for direct POST request data
    const checkDirectPostRequest = () => {
      // This function would be called by an event listener set up in index.js or App.js
      // that listens for POST data and triggers authentication
      document.addEventListener('post-auth-data', async (e) => {
        try {
          if (e.detail && e.detail.data) {
            const success = await loginWithPostData(e.detail.data);
            if (success) {
              navigate('/');
            }
          }
        } catch (err) {
          console.error('Error processing direct POST data:', err);
        }
      });
    };

    handlePostAuth();
    checkDirectPostRequest();

    // Cleanup event listener
    return () => {
      document.removeEventListener('post-auth-data', () => {});
    };
  }, [loginWithPostData, postAuthAttempted]);

  return (
    <div className="login-container">
      <h2>DeepR Dashboard Login</h2>
      <div className="login-description">
        <p>Please enter your AWS Cognito authentication token to access the dashboard.</p>
      </div>
      
      <div className="login-mode-toggle">
        <label className="toggle-label">
          <input
            type="checkbox"
            checked={useAdvancedMode}
            onChange={() => setUseAdvancedMode(!useAdvancedMode)}
            disabled={loading || authLoading}
          />
          Advanced Mode (POST Request Format)
        </label>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="token">Authentication Token:</label>
          <input
            id="token"
            type="text"
            value={token}
            onChange={e => setToken(e.target.value)}
            required
            autoFocus
            placeholder="Enter your authentication token"
            disabled={loading || authLoading}
          />
        </div>
        
        {useAdvancedMode && (
          <>
            <div className="form-group">
              <label htmlFor="userData">User Data (JSON):</label>
              <div className="example-json">
                <div className="example-header">
                  <span>Example User Data:</span>
                  <button 
                    type="button" 
                    className="copy-button"
                    onClick={() => {
                      setUserData(JSON.stringify({user_id: "user123", user_name: "John Doe"}, null, 2));
                    }}
                  >
                    Copy Example
                  </button>
                </div>
                <pre>{JSON.stringify({user_id: "user123", user_name: "John Doe"}, null, 2)}</pre>
              </div>
              <textarea
                id="userData"
                value={userData}
                onChange={e => setUserData(e.target.value)}
                placeholder={'{"user_id": "user123", "user_name": "John Doe"}'}
                disabled={loading || authLoading}
                rows={4}
              />
              <small className="form-help">Optional: Enter user data in JSON format</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="serverData">Server Data (JSON):</label>
              <div className="example-json">
                <div className="example-header">
                  <span>Example Server Data:</span>
                  <button 
                    type="button" 
                    className="copy-button"
                    onClick={() => {
                      setServerData(JSON.stringify([
                        {server_id: "server123", server_name: "Production Server"},
                        {server_id: "server456", server_name: "Development Server"}
                      ], null, 2));
                    }}
                  >
                    Copy Example
                  </button>
                </div>
                <pre>{JSON.stringify([
                  {server_id: "server123", server_name: "Production Server"},
                  {server_id: "server456", server_name: "Development Server"}
                ], null, 2)}</pre>
              </div>
              <textarea
                id="serverData"
                value={serverData}
                onChange={e => setServerData(e.target.value)}
                placeholder={'[{"server_id": "server123", "server_name": "Production Server"}]'}
                disabled={loading || authLoading}
                rows={4}
              />
              <small className="form-help">Optional: Enter server data in JSON format (array of server objects)</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="wsConnection">WebSocket Connection (JSON):</label>
              <div className="example-json">
                <div className="example-header">
                  <span>Example WebSocket Connection:</span>
                  <button 
                    type="button" 
                    className="copy-button"
                    onClick={() => {
                      setWsConnection(JSON.stringify({
                        ip_address: "127.0.0.1",
                        port: 8081
                      }, null, 2));
                    }}
                  >
                    Copy Example
                  </button>
                </div>
                <pre>{JSON.stringify({
                  ip_address: "127.0.0.1",
                  port: 8081
                }, null, 2)}</pre>
              </div>
              <textarea
                id="wsConnection"
                value={wsConnection}
                onChange={e => setWsConnection(e.target.value)}
                placeholder='{"ip_address": "127.0.0.1", "port": 8081}'
                disabled={loading || authLoading}
                rows={4}
              />
              <small className="form-help">Optional: Enter WebSocket connection information in JSON format</small>
            </div>
          </>
        )}
        
        <button 
          type="submit" 
          disabled={loading || authLoading || !token.trim()}
          className="login-button"
        >
          {loading || authLoading ? 'Authenticating...' : 'Login'}
        </button>
      </form>
      
      {error && <div className="login-error">{error}</div>}
      
      {useAdvancedMode && (
        <div className="json-preview">
          <h3>POST Request Preview:</h3>
          <pre>
            {(() => {
              try {
                // Try to create a preview of the POST request using ConnectionService
                let wsConnectionObj = null;
                try {
                  if (wsConnection && wsConnection.trim() !== '') {
                    wsConnectionObj = JSON.parse(wsConnection);
                  }
                } catch (e) {
                  // Invalid JSON, will use placeholder
                }
                
                let userDataObj = null;
                try {
                  if (userData && userData.trim() !== '') {
                    userDataObj = JSON.parse(userData);
                  }
                } catch (e) {
                  // Invalid JSON, will use placeholder
                }
                
                let serverDataArr = null;
                try {
                  if (serverData && serverData.trim() !== '') {
                    serverDataArr = JSON.parse(serverData);
                  }
                } catch (e) {
                  // Invalid JSON, will use placeholder
                }
                
                const preview = ConnectionService.formatPostData({
                  token: token || "<authentication_token>",
                  userData: userDataObj,
                  serverData: serverDataArr,
                  wsConnection: wsConnectionObj
                });
                return JSON.stringify(preview, null, 2);
              } catch (error) {
                // If there's an error parsing the JSON, show a helpful message
                return `{
  "token": "${token || '<authentication_token>'}",
  "user_data": ${userData ? '"<Invalid JSON format>"' : '{ "user_id": "<user_id>", "user_name": "<user_name>" }'},
  "server_data": ${serverData ? '"<Invalid JSON format>"' : '[{ "server_id": "<server_id>", "server_name": "<server_name>" }]'},
  "ws_connection": ${wsConnection ? '"<Invalid JSON format>"' : '{ "ip_address": "<ip_address>", "port": "<port>" }'}
}`;
              }
            })()}
          </pre>
        </div>
      )}
    </div>
  );
}

export default Login;
